import { z } from "zod";
import { memoryKindSchema, memoryTextSchema } from "./memory.ts";
import {
  MAX_TASK_COMMENT_CHARS,
  MAX_TASK_DESCRIPTION_CHARS,
  MAX_TASK_TITLE_CHARS,
  taskCommentBodySchema,
  taskDescriptionSchema,
  taskDueDateSchema,
  taskIdSchema,
  taskListStatusSchema,
  taskStatusSchema,
  taskTitleSchema,
} from "./tasks.ts";

/**
 * The agent's task and memory tools (openspec change `agent-turn`, specs `agent-task-tools` and
 * `agent-memory`, D9–D13). All five are internal (no provider, no egress): `list_tasks` reads
 * the project's tasks live as data with per-turn `K#` references; `create_task` and
 * `update_task` store a proposal the user confirms with a click, never a task change;
 * `recall_memory` searches the memories the turn could have been given; `propose_memory` is the
 * explicit path to the existing memory proposal card.
 */
export const LIST_TASKS_TOOL_NAME = "list_tasks";
export const CREATE_TASK_TOOL_NAME = "create_task";
export const UPDATE_TASK_TOOL_NAME = "update_task";
export const RECALL_MEMORY_TOOL_NAME = "recall_memory";
export const PROPOSE_MEMORY_TOOL_NAME = "propose_memory";

export const TASK_TOOL_NAMES = [
  LIST_TASKS_TOOL_NAME,
  CREATE_TASK_TOOL_NAME,
  UPDATE_TASK_TOOL_NAME,
] as const;
export const MEMORY_TOOL_NAMES = [RECALL_MEMORY_TOOL_NAME, PROPOSE_MEMORY_TOOL_NAME] as const;

export const AGENT_TASK_LIMITS = {
  /** Rows `list_tasks` returns at most (D10). */
  listRows: 20,
  listQuery: 100,
  /** Task proposals per turn (D11, `turn_limit`). */
  proposalsPerTurn: 3,
  /** The assignee as the model names it ("me" or a name as listed). */
  assignee: 200,
  /** Memories `recall_memory` returns at most (D12). */
  recallRows: 10,
  recallQuery: 200,
} as const;

/** Days an undecided task proposal stays open (D11), like a memory proposal. */
export const TASK_PROPOSAL_TTL_MS = 7 * 24 * 60 * 60 * 1000;

/** A per-turn task reference (`K1`…), mapped to a task ID on the server only. */
export const taskRefSchema = z
  .string()
  .trim()
  .regex(/^K[1-9]\d{0,2}$/i, "a K# reference from list_tasks")
  .transform((ref) => ref.toUpperCase());

/** `list_tasks` input (D10). */
export const listTasksInputSchema = z.object({
  status: taskListStatusSchema.optional(),
  assignee: z.enum(["me", "unassigned", "anyone"]).optional(),
  query: z.string().trim().min(1).max(AGENT_TASK_LIMITS.listQuery).optional(),
  limit: z.int().min(1).max(AGENT_TASK_LIMITS.listRows).optional(),
});
export type ListTasksInput = z.infer<typeof listTasksInputSchema>;

/** One task as the model reads it: data, never instructions, never an identifier. */
export const listedTaskSchema = z.object({
  ref: z.string(),
  title: z.string(),
  status: taskStatusSchema,
  dueDate: z.string().nullable(),
  assigneeName: z.string().nullable(),
  isMine: z.boolean(),
});
export type ListedTask = z.infer<typeof listedTaskSchema>;

export const listTasksOutputSchema = z.union([
  z.object({ tasks: z.array(listedTaskSchema), note: z.string() }),
  z.object({ error: z.string() }),
]);
export type ListTasksOutput = z.infer<typeof listTasksOutputSchema>;

/** `create_task` input (D11): the fields of a new task, checked with the task rules. */
export const createTaskToolInputSchema = z.object({
  title: z.string().max(4 * MAX_TASK_TITLE_CHARS),
  description: z
    .string()
    .max(4 * MAX_TASK_DESCRIPTION_CHARS)
    .optional(),
  dueDate: z.string().max(20).optional(),
  /** "me" or a person's name as `list_tasks` or the user wrote it. */
  assignee: z.string().max(AGENT_TASK_LIMITS.assignee).optional(),
});
export type CreateTaskToolInput = z.infer<typeof createTaskToolInputSchema>;

/** `update_task` input (D11): a listed task's reference and the fields to change. */
export const updateTaskToolInputSchema = z.object({
  ref: taskRefSchema,
  status: z.string().max(20).optional(),
  /** Null clears the due date or the assignee. */
  dueDate: z.string().max(20).nullable().optional(),
  assignee: z.string().max(AGENT_TASK_LIMITS.assignee).nullable().optional(),
  title: z
    .string()
    .max(4 * MAX_TASK_TITLE_CHARS)
    .optional(),
  description: z
    .string()
    .max(4 * MAX_TASK_DESCRIPTION_CHARS)
    .optional(),
  comment: z
    .string()
    .max(4 * MAX_TASK_COMMENT_CHARS)
    .optional(),
});
export type UpdateTaskToolInput = z.infer<typeof updateTaskToolInputSchema>;

/**
 * What `create_task` and `update_task` return: a proposal shown to the user (the fields that
 * were dropped and why, for the model to say), or nothing stored with the reason.
 */
export const taskProposalToolOutputSchema = z.union([
  z.object({
    proposed: z.literal(true),
    /** The proposal's per-turn reference (`P1`…). */
    ref: z.string(),
    /** Fields left out of the proposal, with why ("assignee: not a member of this project"). */
    dropped: z.array(z.string()).optional(),
    note: z.string(),
  }),
  z.object({ proposed: z.literal(false), error: z.string() }),
]);
export type TaskProposalToolOutput = z.infer<typeof taskProposalToolOutputSchema>;

/** A person a proposal names, as the task list shows them. */
export const proposalPersonSchema = z.object({
  userId: z.uuid(),
  name: z.string().nullable(),
});
export type ProposalPerson = z.infer<typeof proposalPersonSchema>;

/** The fields of a proposed new task, validated with the task rules. */
export const createTaskProposalPayloadSchema = z.object({
  title: taskTitleSchema,
  description: taskDescriptionSchema.nullable(),
  dueDate: taskDueDateSchema.nullable(),
  assignee: proposalPersonSchema.nullable(),
});

/**
 * The changes to an existing task; absent fields stay as they are, null clears. `taskTitle` is
 * the task's title when the agent listed it (the proposal's label covers the task's).
 */
export const updateTaskProposalPayloadSchema = z.object({
  taskTitle: z.string(),
  title: taskTitleSchema.optional(),
  description: taskDescriptionSchema.nullable().optional(),
  status: taskStatusSchema.optional(),
  dueDate: taskDueDateSchema.nullable().optional(),
  assignee: proposalPersonSchema.nullable().optional(),
  comment: taskCommentBodySchema.optional(),
});

export const TASK_PROPOSAL_KINDS = ["create", "update"] as const;
export const taskProposalKindSchema = z.enum(TASK_PROPOSAL_KINDS);
export type TaskProposalKind = z.infer<typeof taskProposalKindSchema>;

export const taskProposalPayloadSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("create"), fields: createTaskProposalPayloadSchema }),
  z.object({ kind: z.literal("update"), fields: updateTaskProposalPayloadSchema }),
]);
export type TaskProposalPayload = z.infer<typeof taskProposalPayloadSchema>;
export type CreateTaskProposalFields = z.infer<typeof createTaskProposalPayloadSchema>;
export type UpdateTaskProposalFields = z.infer<typeof updateTaskProposalPayloadSchema>;

export const TASK_PROPOSAL_STATUSES = ["pending", "applied", "dismissed", "expired"] as const;
export const taskProposalStatusSchema = z.enum(TASK_PROPOSAL_STATUSES);
export type TaskProposalStatus = z.infer<typeof taskProposalStatusSchema>;

export const taskProposalIdSchema = z.uuid().brand<"TaskProposalId">();
export type TaskProposalId = z.infer<typeof taskProposalIdSchema>;

/**
 * A task proposal as the conversation's creator sees it under the answer (D11): its fields,
 * its state, and the task it created or changed once applied. Never shown to anyone else.
 */
export const taskProposalViewSchema = z.object({
  id: taskProposalIdSchema,
  messageId: z.string(),
  payload: taskProposalPayloadSchema,
  status: taskProposalStatusSchema,
  /** The task changed (an update) or created (once applied). */
  taskId: taskIdSchema.nullable(),
  expiresAt: z.string(),
  trust: z.enum(["internal", "generated", "external"]),
  sourceCount: z.number().int().nonnegative(),
});
export type TaskProposalView = z.infer<typeof taskProposalViewSchema>;

/** The proposals streamed after an answer, one part each; ignored by older clients. */
export const TASK_PROPOSAL_DATA_NAME = "task-proposal";
export const TASK_PROPOSAL_PART = `data-${TASK_PROPOSAL_DATA_NAME}` as const;
export const taskProposalPartId = (id: string) => `task-proposal-${id}`;
export const taskProposalPartSchema = z.object({
  type: z.literal(TASK_PROPOSAL_PART),
  id: z.string(),
  data: taskProposalViewSchema,
});
export type TaskProposalPart = z.infer<typeof taskProposalPartSchema>;

/**
 * `POST …/task-proposals/:id/apply`: the user's inline edits of the title, assignee and due
 * date before applying (absent keeps the proposal's value; null clears).
 */
export const applyTaskProposalRequestSchema = z.strictObject({
  title: taskTitleSchema.optional(),
  dueDate: taskDueDateSchema.nullable().optional(),
  assigneeUserId: z.uuid().nullable().optional(),
});
export type ApplyTaskProposalRequest = z.infer<typeof applyTaskProposalRequestSchema>;

/** `recall_memory` input (D12). */
export const recallMemoryInputSchema = z.object({
  query: z.string().trim().min(1).max(AGENT_TASK_LIMITS.recallQuery),
  scope: z.enum(["personal", "project", "both"]).optional(),
});
export type RecallMemoryInput = z.infer<typeof recallMemoryInputSchema>;

export const recalledMemorySchema = z.object({
  text: z.string(),
  kind: memoryKindSchema,
  scope: z.enum(["personal", "project"]),
});
export type RecalledMemory = z.infer<typeof recalledMemorySchema>;

export const recallMemoryOutputSchema = z.union([
  z.object({ memories: z.array(recalledMemorySchema), note: z.string() }),
  z.object({ error: z.string() }),
]);
export type RecallMemoryOutput = z.infer<typeof recallMemoryOutputSchema>;

/** `propose_memory` input (D13). */
export const proposeMemoryInputSchema = z.object({
  text: memoryTextSchema,
  kind: memoryKindSchema,
  scope: z.enum(["personal", "project"]),
});
export type ProposeMemoryInput = z.infer<typeof proposeMemoryInputSchema>;

export const proposeMemoryOutputSchema = z.union([
  z.object({ proposed: z.literal(true), note: z.string() }),
  z.object({ proposed: z.literal(false), error: z.string() }),
]);
export type ProposeMemoryOutput = z.infer<typeof proposeMemoryOutputSchema>;
